import React, { Fragment, useEffect, useRef, useState } from "react";
import { TiArrowSortedDown } from "react-icons/ti";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getCollaboratorsSuggestions } from "../../api";
import { getPartners } from "../../api/partners";
import {
  CLIENT_CRM_TYPE_FILTER,
  CLIENT_GROUP_CRM_FILTER,
  DELETED_STATUS,
  FINANCIAL_SORT_TYPES,
  IS_ENTITY_STATES,
} from "../../config";
import _ from "../../i18n";
import {
  parsePreference,
  renderTeamYears,
  useOnClickOutside,
  viewDataBy,
} from "../../utils";
import TTPFilterHorizontalBar from "../common/Filter/TTPFilterHorizontalBar";
import "./FinancialSheet.local.scss";
import { SORT_TYPES } from "./Constants";

const FinancialSheetFilter = ({
  token,
  navCommunity,
  filter,
  setFilter,
  collaborator,
  preferences,
  ...props
}) => {
  const ref = useRef();
  useOnClickOutside(ref, () => setYearDivIsVisible(false));
  const { user, loggedAs } = useSelector((state) => state.auth);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [collaboratorOptions, setCollaboratorOptions] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [yearDivIsVisible, setYearDivIsVisible] = useState(false);

  useEffect(() => {
    const { partner } = filter;
    if (partner) {
      const options = collaborators.filter((c) => c.partner == partner);

      setCollaboratorOptions(options);
    } else {
      setCollaboratorOptions(collaborators);
    }
  }, [filter.partner, collaborators]);

  let partnersQuery = useQuery(
    ["partnersQuery", token, navCommunity],
    async () => {
      if (token && navCommunity) {
        const response = await getPartners({
          token,
          organizationId: navCommunity?.id,
        });
        const data = response.data.data.map((partner) => ({
          value: partner.id,
          label: partner.name,
          zone: partner.zone,
        }));
        setPartnerOptions(data);
        if (parsePreference(preferences.clients).allowViewDataBy === 11) {
          const zone = data.find((p) => p.value === Number(user?.id)).zone;
          setFilter({ ...filter, partnerZone: zone });
        }
        return data;
      }
    },
    {
      enabled: !!token && !!navCommunity,
    }
  );

  let collaboratorsdQuery = useQuery(
    ["getCollaborators", token, navCommunity],
    async () => {
      if (token && navCommunity) {
        try {
          const response = await getCollaboratorsSuggestions(
            token,
            navCommunity.id
          );
          let data = response.data.data;
          let list = viewDataBy(
            preferences,
            data,
            collaborator,
            "dynamicTable"
          );
          data = list
            .filter((item) => item !== null)
            .map((t) => {
              return {
                value: t.id,
                label: t.name,
                partner: t?.partner?.value,
                isActif: t.isActif,
              };
            });
          setCollaborators(
            list.map((c) => ({
              value: c.id,
              label: c.name,
              partner: c?.partner?.value,
              isActif: c.isActif,
            }))
          );
          setCollaboratorOptions(data);
        } catch (e) {
          return null;
        }
      }
    }
  );

  const renderYearSelect = () => {
    return (
      <Fragment>
        <div
          ref={ref}
          className={
            "customed_select" +
            " " +
            (yearDivIsVisible ? "customed_select_opened" : "")
          }
          style={{
            background: "#6d7f92",
            color: "#ffffff",
          }}
        >
          <div
            className={"customed_select_header"}
            onClick={() => setYearDivIsVisible(!yearDivIsVisible)}
          >
            <span>{filter.year}</span>
            <div className={"actions"}>
              <div>
                <span className={"action"}>
                  <TiArrowSortedDown />
                </span>
              </div>
            </div>
          </div>
          {yearDivIsVisible && (
            <div className={"customed_select_body"}>
              <div className={"customed_select_body_options"}>
                {renderTeamYears().map((item, index) => (
                  <div
                    key={index}
                    className={"customed_select_body_option"}
                    onClick={() => setFilter({ ...filter, year: item })}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {props.customAddOn}
      </Fragment>
    );
  };

  const handlePartnerChange = (partner) => {
    const zone = partnerOptions.find((p) => p.value === Number(partner))?.zone;
    setFilter({ ...filter, partner: Number(partner), partnerZone: zone });
  };

  const handleSetDeletedStatus = (status) => {
    setFilter({ ...filter, deletedStatus: status });
  };

  const handleTypeCRMClientChange = (items) => {
    setFilter({ ...filter, clientCRMTypes: items });
  };

  const handleGroupCRMClientChange = (items) => {
    setFilter({
      ...filter,
      clientCRMGroup: items,
    });
  };

  const handleSetIsEntityState = (state) => {
    setFilter({ ...filter, isEntity: state });
  };

  const handleSortByClientChange = (items) => {
    setFilter({ ...filter, sortBy: items });
  };

  const handleCollaboratorsChange = (items) => {
    const actifs = collaborators
      .filter((item) => item.isActif)
      .map((item) => item.value);

    const updatedSelection = ["SUPERADMIN", "ADMIN"].includes(loggedAs)
      ? [...items]
      : [...actifs, ...items];

    setFilter({ ...filter, collaborators: [...new Set(updatedSelection)] });
  };

  const filters = [
    {
      type: "SELECT",
      options: {
        isSimple: false,
        isMulti: true,
        isClearable: false,
        isSearch: true,
        placeholder: _("collaborator"),
        options: collaboratorOptions,
        values: filter.collaborators,
        action: handleCollaboratorsChange,
      },
    },
    {
      grid: 3,
      type: "SELECT",
      options: {
        isSimple: true,
        placeholder: _("sortBy"),
        options: SORT_TYPES,
        action: handleSortByClientChange,
        values: filter.sortBy,
      },
    },
    {
      type: "SELECT",
      options: {
        isSimple: true,
        isClearable: true,
        placeholder: _("clientState"),
        options: DELETED_STATUS,
        values: filter.deletedStatus,
        action: handleSetDeletedStatus,
      },
    },
    {
      grid: 3,
      type: "SELECT",
      options: {
        isSimple: true,
        isClearable: true,
        placeholder: _("clientType"),
        options: IS_ENTITY_STATES,
        values: filter.isEntity,
        action: handleSetIsEntityState,
      },
    },
    {
      grid: 3,
      type: "SELECT",
      options: {
        isSimple: false,
        isMulti: true,
        isClearable: true,
        placeholder: _("Type CRM Client"),
        options: CLIENT_CRM_TYPE_FILTER,
        values: filter.clientCRMTypes,
        action: handleTypeCRMClientChange,
      },
    },
    {
      type: "SELECT",
      options: {
        isSimple: false,
        isMulti: true,
        isClearable: true,
        placeholder: _("clients CRM Group"),
        options: CLIENT_GROUP_CRM_FILTER,
        values: filter.clientCRMGroup,
        // defaultValues: [
        //   _("AiSBL"),
        //   _("ASBL"),
        //   _("ASBL - Consultation"),
        //   _("ASBL - Partenaire"),
        //   _("ASBL petite entreprise"),
        //   _("Contact"),
        //   _("Fondation privée"),
        //   _("Indépendants"),
        //   _("Indépendants - Consultation"),
        //   _("Indépendants petite entreprise"),
        //   _("Partenaire"),
        //   _("Particuliers"),
        //   _("Particuliers - Consultation"),
        //   _("Sociétés"),
        //   _("Sociétés - Partenaire"),
        //   _("Sociétés petite entreprise"),
        // ],
        action: handleGroupCRMClientChange,
      },
    },
  ];

  if (
    [1, 11].includes(
      parsePreference(preferences).financialState.allowViewDataBy
    )
  ) {
    filters.unshift({
      type: "SELECT",
      options: {
        isSimple: true,
        isClearable: true,
        isSearch: true,
        isDisabled:
          parsePreference(preferences).financialState.allowViewDataBy === 11,
        placeholder: _("partner"),
        options: partnerOptions,
        values:
          loggedAs === "COLLABORATOR" &&
          parsePreference(preferences).financialState.allowViewDataBy === 11
            ? collaborator?.partner?.value
            : filter.partner,
        action: handlePartnerChange,
      },
    });
  }

  return (
    <div className="row align-center medium-12">
      <div className="medium-12">
        <TTPFilterHorizontalBar
          label={_("nbrFolders")}
          button
          bg="#FFF"
          subBG="F8F9FA"
          searchBox
          filter={filter}
          updateFilterContent={setFilter}
          filters={filters}
          nbResult={filter.nbResults}
          pageSize={filter.pageSize}
          customAddOn={renderYearSelect()}
          cssClass="ttp-financial-filter-horizontal-bar"
        />
      </div>
    </div>
  );
};

export default FinancialSheetFilter;
